/**
 * Metric Definition List Component
 * Displays all metric definitions grouped by type (builtin vs custom)
 */

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';
import { useEvalMetrics, useDeleteMetricDefinition } from '../hooks/useEvalMetrics';
import { MetricPromptViewer } from './MetricPromptViewer';
import { MetricDefinitionEditor } from './MetricDefinitionEditor';
import type { MetricDefinition } from '@/types/evalScenarios';

export function MetricDefinitionList() {
  const { messages } = useI18n();
  const t = messages.evalScenarios.metricDefinitions;
  const tc = messages.common;

  const { data, isLoading, error, refetch } = useEvalMetrics();
  const deleteMutation = useDeleteMetricDefinition();

  const [viewingMetric, setViewingMetric] = useState<MetricDefinition | null>(null);
  const [editingMetric, setEditingMetric] = useState<MetricDefinition | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const metrics = data?.metrics || [];
  const builtinMetrics = metrics.filter((m) => m.metric_type === 'builtin');
  const customMetrics = metrics.filter((m) => m.metric_type !== 'builtin');

  const handleDelete = async (metric: MetricDefinition) => {
    if (metric.is_system) {
      alert(t?.deleteSystemError || 'System metrics cannot be deleted');
      return;
    }

    if (!confirm(t?.deleteConfirm || 'Are you sure you want to delete this metric?')) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(metric.name);
    } catch (err) {
      console.error('Failed to delete metric:', err);
    }
  };

  const renderMetricCard = (metric: MetricDefinition, isBuiltin: boolean) => {
    const thresholdText = metric.threshold_direction === 'lower'
      ? `≤ ${(metric.default_threshold * 100).toFixed(0)}%`
      : `≥ ${(metric.default_threshold * 100).toFixed(0)}%`;

    const agentsText = metric.applicable_agent_types.length === 0
      ? t?.allAgents || 'All'
      : metric.applicable_agent_types.join(', ');

    return (
      <div
        key={metric.id}
        className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">
              {isBuiltin ? '🔒' : '✏️'}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {metric.display_name}
                </span>
                <span className="text-xs text-gray-500 font-mono">
                  ({metric.name})
                </span>
              </div>
              {metric.description && (
                <p className="text-sm text-gray-600 mt-1">
                  {metric.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className={`text-sm px-2 py-0.5 rounded ${
              metric.threshold_direction === 'lower'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {thresholdText}
            </span>

            {isBuiltin ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setViewingMetric(metric)}
              >
                {t?.viewPrompt || 'View'}
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditingMetric(metric)}
                >
                  {t?.editMetric || 'Edit'}
                </Button>
                {!metric.is_system && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(metric)}
                    disabled={deleteMutation.isPending}
                  >
                    {t?.deleteMetric || 'Delete'}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
          <span>
            {t?.applicableAgents || 'Agents'}: {agentsText}
          </span>
          {metric.is_system && (
            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
              {t?.systemMetric || 'System'}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-24 bg-gray-200 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-4">
        <div className="text-center text-red-600">
          {tc.error}: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
        <div className="text-center mt-2">
          <Button variant="secondary" onClick={() => refetch()}>
            {tc.retry}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            {t?.pageTitle || 'Metric Definitions'}
          </h2>
          <p className="text-sm text-gray-600">
            {t?.pageDescription || 'Manage evaluation metric definitions'}
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          {t?.createMetric || 'Create Custom Metric'}
        </Button>
      </div>

      {/* Built-in Metrics */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <span>🔒</span>
          {t?.builtinMetrics || 'Built-in Metrics'}
          <span className="text-gray-400">({builtinMetrics.length})</span>
        </h3>
        <div className="space-y-3">
          {builtinMetrics.map((metric) => renderMetricCard(metric, true))}
        </div>
      </div>

      {/* Custom Metrics */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <span>✏️</span>
          {t?.customMetrics || 'Custom Metrics (GEval)'}
          <span className="text-gray-400">({customMetrics.length})</span>
        </h3>
        {customMetrics.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-4xl mb-2">📝</div>
            <div className="text-gray-500">
              {t?.noMetrics || 'No custom metrics yet'}
            </div>
            <Button
              className="mt-4"
              onClick={() => setIsCreating(true)}
            >
              {t?.createMetric || 'Create Custom Metric'}
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {customMetrics.map((metric) => renderMetricCard(metric, false))}
          </div>
        )}
      </div>

      {/* Prompt Viewer Modal */}
      {viewingMetric && (
        <MetricPromptViewer
          metric={viewingMetric}
          isOpen={!!viewingMetric}
          onClose={() => setViewingMetric(null)}
        />
      )}

      {/* Editor Modal */}
      {(editingMetric || isCreating) && (
        <MetricDefinitionEditor
          metric={editingMetric}
          isOpen={!!editingMetric || isCreating}
          onClose={() => {
            setEditingMetric(null);
            setIsCreating(false);
          }}
          onSuccess={() => {
            setEditingMetric(null);
            setIsCreating(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
